<template>
  <!-- component:component/SidebarComponents.html -->
  <div class="wrapper">
    <nav class="sidebar sidebar-offcanvas" id="sidebar">
      <div
        class="sidebar-brand-wrapper d-none d-lg-flex align-items-center justify-content-between px-3 fixed-top"
      >
        <router-link
          :to="{ name: 'Dashboard' }"
          class="sidebar-brand brand-logo d-flex align-items-center gap-2 text-decoration-none"
        >
          <div class="kh-pennant">
            <i class="mdi mdi-shield-crown-outline" style="color: #fbbf24; font-size: 18px;"></i>
          </div>
          <div class="text-left">
            <div class="kh-title text-white" style="font-size: 15px; line-height: 1.05;">GAMITEAM</div>
            <div style="font-family: 'Chakra Petch', sans-serif; font-size: 9px; font-weight: 800; color: #8fa3c2; letter-spacing: 0.08em;">ROYAL CITADEL</div>
          </div>
        </router-link>
        <router-link :to="{ name: 'Dashboard' }" class="sidebar-brand brand-logo-mini">
          <div class="kh-pennant">
            <i class="mdi mdi-shield-crown-outline" style="color: #fbbf24; font-size: 16px;"></i>
          </div>
        </router-link>
        <span class="badge badge-warning" style="font-size: 9px;">RPG v2.5</span>
      </div>

      <ul class="nav">
        <!-- ============================================================== -->
        <!-- 1. GAMITEAM SUPERADMIN (SaaS Platform Operator) NAVIGATION     -->
        <!-- ============================================================== -->
        <template v-if="roleSuperadmin">
          <li class="nav-item nav-category">
            <span class="nav-link font-weight-bold text-warning text-uppercase" style="letter-spacing: 0.5px;">
              {{ $t('nav.superadminSection') }}
            </span>
          </li>

          <!-- Platform Dashboard -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'Dashboard' }">
            <router-link :to="{ name: 'Dashboard' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-speedometer text-info"></i>
              </span>
              <span class="menu-title">{{ $t('nav.dashboard') }}</span>
            </router-link>
          </li>

          <!-- Inbound Leads & Registrations -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'InboundLeads' }">
            <router-link :to="{ name: 'InboundLeads' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-inbox-arrow-down text-warning"></i>
              </span>
              <span class="menu-title">{{ $t('nav.inboundLeads') }}</span>
            </router-link>
          </li>

          <!-- Cross-Enterprise Game Setting Benchmarks -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'GameSettingBenchmarks' }">
            <router-link :to="{ name: 'GameSettingBenchmarks' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-chart-bell-curve-cumulative text-info"></i>
              </span>
              <span class="menu-title">
                {{ $t('nav.gameBenchmarks') }}
              </span>
            </router-link>
          </li>

          <!-- System Audit Logs -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'AuditLogs' }">
            <router-link :to="{ name: 'AuditLogs' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-shield-check text-warning"></i>
              </span>
              <span class="menu-title">{{ $t('nav.systemAuditLogs') }}</span>
            </router-link>
          </li>

          <!-- Knowledge Hub & Glosarium -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'Glossary' }">
            <router-link :to="{ name: 'Glossary' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-book-open-page-variant text-info"></i>
              </span>
              <span class="menu-title">{{ $t('nav.glossary') }}</span>
            </router-link>
          </li>

          <!-- Client Enterprises (Mitra) -->
          <li class="nav-item menu-items" :class="{ active: isOwnerRoute }">
            <a
              class="nav-link"
              data-bs-toggle="collapse"
              href="#owner-menu"
              :aria-expanded="isOwnerRoute ? 'true' : 'false'"
              aria-controls="owner-menu"
            >
              <span class="menu-icon">
                <i class="mdi mdi-domain text-warning"></i>
              </span>
              <span class="menu-title">{{ $t('nav.clientCompanies') }}</span>
              <i class="mdi mdi-chevron-down ms-auto"></i>
            </a>
            <div class="collapse" :class="{ show: isOwnerRoute }" id="owner-menu">
              <ul class="nav flex-column sub-menu">
                <li class="nav-item">
                  <router-link :to="{ name: 'ListOwner' }" class="nav-link">
                    {{ $t('superadmin.allCompanies') }}
                  </router-link>
                </li>
                <li class="nav-item">
                  <router-link :to="{ name: 'CreateOwner' }" class="nav-link">
                    {{ $t('nav.createCompany') }}
                  </router-link>
                </li>
              </ul>
            </div>
          </li>

          <!-- SaaS Billing & Subscriptions -->
          <li class="nav-item nav-category mt-2">
            <span class="nav-link font-weight-bold text-muted text-uppercase" style="font-size: 10px;">
              {{ $t('nav.billingAndFinance') }}
            </span>
          </li>

          <!-- Subscription Packages -->
          <li class="nav-item menu-items" :class="{ active: isPackageRoute }">
            <router-link :to="{ name: 'ListPackage' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-package-variant text-primary"></i>
              </span>
              <span class="menu-title">{{ $t('nav.subscriptionPackages') }}</span>
            </router-link>
          </li>

          <!-- Invoice Approvals (Manual Bank Transfer Verification) -->
          <li class="nav-item menu-items" :class="{ active: isBillingRoute }">
            <router-link :to="{ name: 'Billing' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-cash-check text-success"></i>
              </span>
              <span class="menu-title">
                {{ $t('nav.billingApprovals') }}
              </span>
            </router-link>
          </li>

          <!-- Player Quota Allocation -->
          <li class="nav-item menu-items" :class="{ active: isQuotaRoute }">
            <router-link :to="{ name: 'Quota' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-account-group-outline text-warning"></i>
              </span>
              <span class="menu-title">
                {{ $t('nav.playerQuota') }}
              </span>
            </router-link>
          </li>

          <!-- Payment Channels & Gateways -->
          <li class="nav-item menu-items" :class="{ active: isPaymentRoute }">
            <router-link :to="{ name: 'ListPayment' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-credit-card-settings-outline text-info"></i>
              </span>
              <span class="menu-title">
                {{ $t('nav.paymentChannels') }}
              </span>
            </router-link>
          </li>

          <!-- Discount Vouchers -->
          <li class="nav-item menu-items" :class="{ active: isDiscountRoute }">
            <router-link :to="{ name: 'discount' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-ticket-percent text-danger"></i>
              </span>
              <span class="menu-title">{{ $t('nav.discountVouchers') }}</span>
            </router-link>
          </li>

          <!-- Creator Ecosystem & Revenue Share -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'CreatorEcosystem' }">
            <router-link :to="{ name: 'CreatorEcosystem' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-account-star text-warning"></i>
              </span>
              <span class="menu-title">{{ $t('nav.creatorEcosystem') }}</span>
            </router-link>
          </li>

          <!-- Platform Users & Directory -->
          <li class="nav-item nav-category mt-2">
            <span class="nav-link font-weight-bold text-muted text-uppercase" style="font-size: 10px;">
              {{ $t('nav.systemAndSecurity') }}
            </span>
          </li>

          <li class="nav-item menu-items" :class="{ active: isAdminPlayer }">
            <router-link :to="{ path: '/adminplayer' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-account-group text-info"></i>
              </span>
              <span class="menu-title">{{ $t('nav.gamePlayer') }}</span>
            </router-link>
          </li>

          <li class="nav-item menu-items" :class="{ active: isAdminMakerRoute }">
            <router-link :to="{ path: '/adminmaker' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-shield-account text-success"></i>
              </span>
              <span class="menu-title">{{ $t('nav.gameMaker') }}</span>
            </router-link>
          </li>

          <!-- Virtual Realms Catalog -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'RealmMarketplace' }">
            <router-link :to="{ name: 'RealmMarketplace' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-gamepad-variant text-warning"></i>
              </span>
              <span class="menu-title">{{ $t('nav.gameRealms') }}</span>
            </router-link>
          </li>
        </template>

        <!-- ============================================================== -->
        <!-- 2. CLIENT ENTERPRISE NAVIGATION (Game Owner & Game Maker)     -->
        <!-- ============================================================== -->
        <template v-else>
          <li class="nav-item nav-category">
            <span class="nav-link font-weight-bold text-info text-uppercase" style="letter-spacing: 0.5px;">
              {{ $t('nav.companyWorkspace') }}
            </span>
          </li>

          <!-- Company Dashboard -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'Dashboard' }">
            <router-link :to="{ name: 'Dashboard' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-speedometer"></i>
              </span>
              <span class="menu-title">{{ $t('nav.dashboard') }}</span>
            </router-link>
          </li>

          <!-- Game Owner Executive Macro Analytics -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'ExecutiveAnalytics' }" v-if="canAccessOwnerAnalytics">
            <router-link :to="{ name: 'ExecutiveAnalytics' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-crown text-warning"></i>
              </span>
              <span class="menu-title font-weight-bold">{{ $t('nav.ownerAnalytics') }}</span>
            </router-link>
          </li>

          <!-- Game Maker Studio & Anti-Cheat Hub -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'MakerStudio' }" v-if="canAccessMakerStudio">
            <router-link :to="{ name: 'MakerStudio' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-hammer-wrench text-success"></i>
              </span>
              <span class="menu-title font-weight-bold">{{ $t('nav.makerStudio') }}</span>
            </router-link>
          </li>

          <!-- Knowledge Hub & Glosarium -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'Glossary' }">
            <router-link :to="{ name: 'Glossary' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-book-open-page-variant text-info"></i>
              </span>
              <span class="menu-title font-weight-bold">{{ $t('nav.glossary') }}</span>
            </router-link>
          </li>

          <!-- Game Settings & Architecture Hub -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'GameSettings' }" v-if="canAccessGameSettings">
            <router-link :to="{ name: 'GameSettings' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-cogs text-warning"></i>
              </span>
              <span class="menu-title font-weight-bold">
                {{ $t('nav.gameSettings') }}
              </span>
            </router-link>
          </li>

          <!-- Squads & Divisions -->
          <li class="nav-item menu-items" :class="{ active: isSquadRoute }" v-if="canAccessSquad">
            <router-link :to="{ name: 'ListSquad' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-account-group text-primary"></i>
              </span>
              <span class="menu-title">{{ $t('nav.squads') }}</span>
            </router-link>
          </li>

          <!-- Game Rules (Aturan Gamifikasi) -->
          <li class="nav-item menu-items" :class="{ active: isRulesRoute }" v-if="canAccessRules">
            <a
              class="nav-link"
              data-bs-toggle="collapse"
              href="#rules-menu"
              :aria-expanded="isRulesRoute ? 'true' : 'false'"
              aria-controls="rules-menu"
            >
              <span class="menu-icon">
                <i class="mdi mdi-book-open-page-variant text-warning"></i>
              </span>
              <span class="menu-title">{{ $t('nav.rules') }}</span>
              <i class="mdi mdi-chevron-down ms-auto"></i>
            </a>
            <div class="collapse" :class="{ show: isRulesRoute }" id="rules-menu">
              <ul class="nav flex-column sub-menu">
                <li class="nav-item">
                  <router-link :to="{ name: 'ListRewards' }" class="nav-link">
                    {{ $t('nav.quests') }}
                  </router-link>
                </li>
                <li class="nav-item">
                  <router-link :to="{ name: 'ListConsequence' }" class="nav-link">
                    {{ $t('nav.consequences') }}
                  </router-link>
                </li>
                <li class="nav-item">
                  <router-link :to="{ name: 'Compensation' }" class="nav-link">
                    {{ $t('nav.compensations') }}
                  </router-link>
                </li>
              </ul>
            </div>
          </li>

          <!-- Approvals Center -->
          <li class="nav-item menu-items" :class="{ active: isApprovalRoute }" v-if="canAccessApproval">
            <a
              class="nav-link"
              data-bs-toggle="collapse"
              href="#approval-menu"
              :aria-expanded="isApprovalRoute ? 'true' : 'false'"
              aria-controls="approval-menu"
            >
              <span class="menu-icon">
                <i class="mdi mdi-check-decagram-outline text-success"></i>
              </span>
              <span class="menu-title">{{ $t('nav.approvals') }}</span>
              <i class="mdi mdi-chevron-down ms-auto"></i>
            </a>
            <div class="collapse" :class="{ show: isApprovalRoute }" id="approval-menu">
              <ul class="nav flex-column sub-menu">
                <li class="nav-item">
                  <router-link :to="{ name: 'RewardsApproval' }" class="nav-link">
                    {{ $t('nav.questApprovals') }}
                  </router-link>
                </li>
                <li class="nav-item">
                  <router-link :to="{ name: 'ConsequenceApproval' }" class="nav-link">
                    {{ $t('nav.consequenceApprovals') }}
                  </router-link>
                </li>
                <li class="nav-item">
                  <router-link :to="{ name: 'PunishmentApproval' }" class="nav-link">
                    {{ $t('nav.punishmentApprovals') }}
                  </router-link>
                </li>
                <li class="nav-item">
                  <router-link :to="{ name: 'CompensationApproval' }" class="nav-link">
                    {{ $t('nav.compensationApprovals') }}
                  </router-link>
                </li>
              </ul>
            </div>
          </li>

          <!-- Reward Shop Catalog -->
          <li class="nav-item menu-items" :class="{ active: isRedeemRoute }" v-if="canAccessRedeem">
            <router-link :to="{ name: 'ListRedeem' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-wallet-giftcard text-danger"></i>
              </span>
              <span class="menu-title">{{ $t('nav.rewardShop') }}</span>
            </router-link>
          </li>

          <!-- Virtual Realms 3D Marketplace -->
          <li class="nav-item menu-items" :class="{ active: $route.name === 'RealmMarketplace' }">
            <router-link :to="{ name: 'RealmMarketplace' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-gamepad-variant text-warning"></i>
              </span>
              <span class="menu-title">
                {{ $t('nav.gameRealms') }}
              </span>
            </router-link>
          </li>

          <!-- Company Personnel -->
          <li class="nav-item nav-category mt-2" v-if="canAccessMaker || canAccessPlayer">
            <span class="nav-link font-weight-bold text-muted text-uppercase" style="font-size: 10px;">
              {{ $t('nav.companyPersonnel') }}
            </span>
          </li>

          <li class="nav-item menu-items" :class="{ active: isMakerRoute }" v-if="canAccessMaker">
            <router-link :to="{ name: 'ListMaker' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-shield-account text-success"></i>
              </span>
              <span class="menu-title">{{ $t('nav.gameMaker') }}</span>
            </router-link>
          </li>

          <li class="nav-item menu-items" :class="{ active: isPlayerRoute }" v-if="canAccessPlayer">
            <router-link :to="{ name: 'ListPlayer' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-account-multiple text-info"></i>
              </span>
              <span class="menu-title">{{ $t('nav.gamePlayer') }}</span>
            </router-link>
          </li>

          <!-- Company Subscription Billing -->
          <li class="nav-item menu-items" :class="{ active: isBillingRoute }" v-if="canAccessBilling">
            <router-link :to="{ name: 'Billing' }" class="nav-link">
              <span class="menu-icon">
                <i class="mdi mdi-credit-card-outline text-warning"></i>
              </span>
              <span class="menu-title">{{ $t('nav.billingAndFinance') }}</span>
            </router-link>
          </li>
        </template>

        <li class="nav-item nav-category mt-3 pt-2 border-top border-subtle">
          <span class="nav-link text-muted d-flex align-items-center justify-content-between" style="font-size: 10px;">
            <span>Gamiteam</span>
            <span class="badge badge-outline-secondary px-2 py-0" style="font-size: 9px;">v2.5 Enterprise</span>
          </span>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
export default {
  name: "SidebarComponents",
  data() {
    return {};
  },
  computed: {
    getUserData() {
      try {
        const raw = this.$store.state.user || localStorage.getItem("user");
        if (!raw) return null;
        let parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
        return parsed?.data || parsed || null;
      } catch (e) {
        return null;
      }
    },

    roleSuperadmin() {
      const userRole = this.getUserData?.role || '';
      const currentRoles = userRole.split(',').map(r => r.trim().toLowerCase());
      return currentRoles.includes('superadmin');
    },

    isOwnerRoute() {
      return this.$route.path.startsWith('/owner');
    },

    isMakerRoute() {
      return this.$route.path.startsWith('/maker') && !this.$route.path.includes('/maker/studio');
    },

    isPlayerRoute() {
      return this.$route.path.startsWith('/player');
    },

    isBillingRoute() {
      return this.$route.path.startsWith('/billing');
    },

    isPackageRoute() {
      return this.$route.path.startsWith('/package');
    },

    isQuotaRoute() {
      return this.$route.path.startsWith('/quota') || this.$route.path.startsWith('/addquota');
    },

    isPaymentRoute() {
      return this.$route.path.startsWith('/payment');
    },

    isAdminMakerRoute() {
      return this.$route.path.startsWith('/adminmaker');
    },

    isAdminPlayer() {
      return this.$route.path.startsWith('/adminplayer');
    },

    isDiscountRoute() {
      return this.$route.path.startsWith('/discount');
    },

    isSquadRoute() {
      return this.$route.path.startsWith('/squad');
    },

    isRedeemRoute() {
      return this.$route.path.startsWith('/redeem');
    },

    isRulesRoute() {
      return this.$route.path.startsWith('/rules');
    },

    isApprovalRoute() {
      return this.$route.path.startsWith('/approval');
    },

    canAccessOwnerAnalytics() {
      return this.hasRole("owner");
    },

    canAccessMakerStudio() {
      return this.hasRole("maker", "owner");
    },

    canAccessSquad() {
      return this.hasRole("owner", "maker");
    },

    canAccessApproval() {
      return this.hasRole("owner", "maker");
    },

    canAccessRules() {
      return this.hasRole("owner", "maker");
    },

    canAccessMaker() {
      return this.hasRole("owner");
    },

    canAccessPlayer() {
      return this.hasRole("owner", "maker");
    },

    canAccessRedeem() {
      return this.hasRole("owner", "maker");
    },

    canAccessBilling() {
      return this.hasRole("owner");
    },

    canAccessGameSettings() {
      return this.hasRole("owner", "maker");
    },
  },
  methods: {
    hasRole(...roles) {
      const userRole = this.getUserData?.role || '';
      const currentRoles = userRole.split(',').map(r => r.trim().toLowerCase());
      return roles.some(role => currentRoles.includes(role.toLowerCase()));
    }
  },
};
</script>
